import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaCalculator, FaChartBar, FaInfoCircle, FaQuestionCircle, FaCheckCircle, FaLightbulb, FaMoneyBillWave, FaQuestion } from 'react-icons/fa';
import Chart from 'react-apexcharts';

const TaxBracketCalculator = () => {
  const [income, setIncome] = useState('');
  const [taxBrackets, setTaxBrackets] = useState([
    { min: 0, max: 9950, rate: 0.10 },
    { min: 9951, max: 40525, rate: 0.12 },
    { min: 40526, max: 86375, rate: 0.22 },
    { min: 86376, max: 164925, rate: 0.24 },
    { min: 164926, max: 209425, rate: 0.32 },
    { min: 209426, max: 523600, rate: 0.35 },
    { min: 523601, max: Infinity, rate: 0.37 }
  ]);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const calculateTax = () => {
    if (!income) {
      setError('Please enter your income.');
      return;
    }

    let taxOwed = 0;
    let remainingIncome = parseFloat(income);

    taxBrackets.forEach((bracket, index) => {
      if (remainingIncome > bracket.min) {
        const taxableIncome = Math.min(remainingIncome, bracket.max) - bracket.min;
        taxOwed += taxableIncome * bracket.rate;
        remainingIncome -= taxableIncome;
      }
    });

    setResults({ taxOwed: taxOwed.toFixed(2) });
    setError('');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value);
  };

  const series = [
    {
      name: 'Income',
      data: [parseFloat(income) || 0]
    },
    {
      name: 'Tax Owed',
      data: [results ? parseFloat(results.taxOwed) : 0]
    }
  ];

  return (
    <div className="calculator-container bg-gray-100 p-4">
      <Helmet>
        <title>Tax Bracket Calculator</title>
        <meta name="description" content="Calculate your tax bracket with our easy-to-use calculator." />
      </Helmet>

      <div className="calculator-header text-center mb-8">
        <FaCalculator className="calculator-icon text-4xl text-green-500" />
        <h1 className="text-3xl font-bold">Tax Bracket Calculator</h1>
      </div>

      <div className="calculator-content max-w-3xl mx-auto bg-white p-8 rounded-lg shadow-lg">
        <div className="calculator-form-section">
          <form onSubmit={(e) => e.preventDefault()} className="calculator-form">
            <div className="input-group mb-4">
              <label htmlFor="income" className="block text-gray-700 font-bold mb-2">
                <FaMoneyBillWave className="inline-block mr-2" /> Annual Income
              </label>
              <input
                type="number"
                id="income"
                name="income"
                value={income}
                onChange={(e) => setIncome(e.target.value)}
                placeholder="Enter your annual income"
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:border-blue-500"
              />
              <span className="info-icon">
                <FaInfoCircle title="Your total annual income" className="text-gray-500 hover:text-blue-500" />
              </span>
            </div>

            <button
              onClick={calculateTax}
              className="calculate-button bg-blue-500 text-white rounded-md py-2 px-4 hover:bg-blue-600 focus:outline-none w-full"
            >
              Calculate Tax
            </button>

            {error && <div className="error-message text-red-500 mt-4">{error}</div>}
          </form>
        </div>

        {results && (
          <div className="results-section mt-8">
            <div className="results-header flex items-center justify-between">
              <h2 className="text-2xl font-bold">Results</h2>
              <FaChartBar className="results-icon text-3xl text-green-500" />
            </div>
            
            <div className="results-chart mt-4">
              <Chart
                options={{
                  chart: {
                    type: 'bar',
                    height: 350,
                    toolbar: {
                      show: false
                    }
                  },
                  plotOptions: {
                    bar: {
                      horizontal: false,
                      columnWidth: '55%',
                      endingShape: 'rounded'
                    },
                  },
                  dataLabels: {
                    enabled: false
                  },
                  stroke: {
                    show: true,
                    width: 2,
                    colors: ['transparent']
                  },
                  xaxis: {
                    categories: ['Values'],
                    labels: {
                      show: false
                    }
                  },
                  yaxis: {
                    labels: {
                      formatter: (value) => formatCurrency(value)
                    }
                  },
                  fill: {
                    opacity: 1
                  },
                  legend: {
                    show: true,
                    position: 'top',
                    horizontalAlign: 'center',
                    offsetX: 0,
                    offsetY: 0
                  }
                }}
                series={series}
                type="bar"
                height={350}
              />
            </div>

            <div className="results-details mt-4">
              <div className="result-item flex items-center justify-between">
                <div className="flex items-center">
                  <FaMoneyBillWave className="mr-2" /> Tax Owed
                </div>
                <div>{formatCurrency(parseFloat(results.taxOwed))}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="calculator-features mt-8 max-w-3xl mx-auto bg-white p-8 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold mb-4">Key Features & Benefits</h2>
        <div className="features-grid grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="feature-item">
            <FaLightbulb className="feature-icon text-3xl text-yellow-500 mb-2" />
            <h3 className="text-lg font-bold">Comprehensive Calculations</h3>
            <p>Calculate your tax based on the applicable tax brackets.</p>
          </div>
          <div className="feature-item">
            <FaChartBar className="feature-icon text-3xl text-green-500 mb-2" />
            <h3 className="text-lg font-bold">Clear Results</h3>
            <p>See a complete breakdown of your tax obligations.</p>
          </div>
          <div className="feature-item">
            <FaCheckCircle className="feature-icon text-3xl text-blue-500 mb-2" />
            <h3 className="text-lg font-bold">Planning Tool</h3>
            <p>Helps in financial planning by showing potential tax liabilities.</p>
          </div>
        </div>
      </div>

      <div className="how-to-use mt-8 max-w-3xl mx-auto bg-white p-8 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold mb-4">How to Use</h2>
        <ol className="usage-steps list-decimal pl-5">
          <li>Enter your annual income</li>
          <li>Click "Calculate Tax" to see your results</li>
        </ol>
      </div>

      <div className="faq-section mt-8 max-w-3xl mx-auto bg-white p-8 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold mb-4">Frequently Asked Questions</h2>
        <div className="faq-grid grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="faq-item">
            <FaQuestion className="faq-icon text-3xl text-gray-500 mb-2" />
            <h3 className="text-lg font-bold">What is a tax bracket?</h3>
            <p>A tax bracket is a range of incomes taxed at a specific rate.</p>
          </div>
          <div className="faq-item">
            <FaQuestion className="faq-icon text-3xl text-gray-500 mb-2" />
            <h3 className="text-lg font-bold">How is tax calculated?</h3>
            <p>Tax is calculated based on the portion of income that falls within each tax bracket.</p>
          </div>
          <div className="faq-item">
            <FaQuestion className="faq-icon text-3xl text-gray-500 mb-2" />
            <h3 className="text-lg font-bold">What are the tax brackets?</h3>
            <p>The tax brackets are predefined ranges of income with corresponding tax rates.</p>
          </div>
          <div className="faq-item">
            <FaQuestion className="faq-icon text-3xl text-gray-500 mb-2" />
            <h3 className="text-lg font-bold">How can I reduce my tax?</h3>
            <p>You can reduce your tax by taking advantage of deductions and credits.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxBracketCalculator;